import { Injectable } from '@angular/core';
import { AppSettingsService } from '@rillion/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { map, Observable, tap } from 'rxjs';
import { InvoiceImage } from '@rillion/api/models/invoice-image-api.models';

const ExtensionContentType: Record<string, string> = {
  xml: 'text/html',
  pdf: 'application/pdf',
};

export interface MediaBlob {
  name?: string;
  objectUrl: string;
  revokeObjectURL: () => void;
}

@Injectable({ providedIn: 'root' })
export class MediaService {
  private http: HttpClient;

  constructor(
    handler: HttpBackend,
    private readonly appSettingsService: AppSettingsService,
  ) {
    // HttpClient without running interceptors to skip BEARER token
    this.http = new HttpClient(handler);
  }

  getMediaBlob(tenantId: string, imageToken: string, extension?: string, listenerId?: string): Observable<MediaBlob> {
    const url = this.getMediaUrl(tenantId, imageToken);

    if (extension === 'xml') {
      return this.http.get(url, { observe: 'body', responseType: 'text' }).pipe(
        map((html) => {
          const bodyEndTagMatch = html?.match(/<\/body>/i);

          if (bodyEndTagMatch) {
            const script = this.getClickInterceptorScript(listenerId);
            html = html.slice(0, bodyEndTagMatch.index) + script + html.slice(bodyEndTagMatch.index);
          }

          return this.createMediaBlob(html, extension);
        }),
      );
    }

    return this.http.get(url, { observe: 'response', responseType: 'blob' }).pipe(map((response) => this.createMediaBlob(response.body, extension)));
  }

  saveMediaAs(tenantId: string, imageToken: string, filename: string) {
    return this.getMediaBlob(tenantId, imageToken).pipe(
      tap((mediaBlob) => {
        const link = document.createElement('a');

        link.href = mediaBlob.objectUrl;
        link.download = filename;

        document.body.appendChild(link);

        link.click();
        link.remove();
        mediaBlob.revokeObjectURL();
      }),
    );
  }

  openMediaInNewTab(invoiceImage: InvoiceImage) {
    const { tenantId, invoiceSasResponses } = invoiceImage;
    const { imageToken } = invoiceSasResponses[0];
    const url = this.getMediaUrl(tenantId, imageToken);

    window.open(url, '_blank');
  }

  private getMediaUrl(tenantId: string, imageToken: string) {
    return `${this.appSettingsService.appSettings.baseUrl}/api/v1.0/Media/${tenantId}/${imageToken}`;
  }

  private createMediaBlob(body: Blob | string, extension: string): MediaBlob {
    const blob = new Blob([body], { ...(extension && { type: ExtensionContentType[extension] }) });
    const objectUrl = URL.createObjectURL(blob);

    return {
      objectUrl,
      revokeObjectURL: () => URL.revokeObjectURL(objectUrl),
    };
  }

  private getClickInterceptorScript(listenerId: string) {
    return `
      <script>
        Array.prototype.slice.call(document.querySelectorAll('a')).forEach(function(anchor) {
          anchor.addEventListener('click', function(e) {
            if (e.target && e.target.href && e.target.href.startsWith('eAttachment')) {
              e.preventDefault();
              e.stopPropagation();
              window.parent.postMessage({ type: 'eAttachmentClick', href: e.target.href, listenerId: '${listenerId}' }, '*');
            }
          });
        });
      </script>`;
  }
}
